import logger from '../../Utils/logger';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PaymentMethod from '../../Components/PaymentMethod/PaymentMethod';
import Timer from '../../Components/Timer/Timer';
import styles from './Style.module.css';
import { SessionExpiredPopup } from '../../Components/Popup/SessionExpiredPopup/SessionExpiredPopup';
import { Header } from '../../Components/Header/Header';
import { useSticky } from '../../Utils/Helper';
// import { DEPOSIT_AMOUNT } from '../../Utils/bookingConfig';
import { useHoldExpiry } from '../../Utils/Hooks/useHoldExpiry';
import { hasActiveBookingHold } from '../../Utils/bookingHold';
import { useBookingTimer } from '../../Context/Timer/useBookingTimer';
import { getSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';

const OVERLAY_CLASSES = ['overlay', 'half', 'overlay-index', 'overflow-hidden'];

const removeOverlayClasses = () => {
  document.body.classList.remove(...OVERLAY_CLASSES);
};

const PaymentOptions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isTimesup, handleSessionExpiredClose } = useHoldExpiry();
  const { setIsTimesup } = useBookingTimer();
  const SGD = 'SGD';

  // Check if this is a voucher payment
  const isVoucherPayment = location.state?.isVoucherPayment;
  const voucherOrderData = location.state?.voucherOrderData;
  const voucherAmount = location.state?.amount;

  const handleBack = () => {
    if (isVoucherPayment) {
      navigate('/review-voucher-purchase');
    } else {
      navigate(-1);
    }
  };

  // Redirect to home if no payment data available
  useEffect(() => {
    if (isVoucherPayment && !voucherOrderData) {
      navigate('/');
    } else if (!isVoucherPayment) {
      const bookingId = getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
      if (!bookingId) {
        navigate("/");
      }
    }
  }, [isVoucherPayment, voucherOrderData, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
    removeOverlayClasses();

    // Skip booking hold checks for voucher payments
    if (isVoucherPayment) {
      logger.log('Voucher payment opened', { orderId: voucherOrderData?.order?.id });
      return;
    }

    const bookingId = getSessionStorageValue(STORAGE_KEYS.BOOKING_ID);
    const timesUp = getSessionStorageValue(STORAGE_KEYS.TIMES_UP);

    logger.log('💳 PaymentOptions mounted - Checking booking hold:', {
      bookingId,
      timesUp,
      isTimesup,
    });

    // Block payment if a prior hold existed but is no longer active.
    const hadPriorHold = Boolean(bookingId) || Boolean(timesUp);
    const hasCurrentHold = hasActiveBookingHold();

    if (hadPriorHold) {
      logger.log('📋 Prior hold detected:', {
        hadPriorHold,
        hasCurrentHold,
        isTimesupState: isTimesup,
      });

      if (!hasCurrentHold && !isTimesup) {
        logger.log('⏰ Hold expired - Setting isTimesup to true');
        setIsTimesup(true);
      } else if (hasCurrentHold) {
        logger.log('✅ Booking hold still active - Payment allowed');
      } else {
        logger.log('⚠️ Already marked as times up - Session expired');
      }
    } else {
      logger.log('✅ No prior hold - Fresh payment session');
    }

    return () => {
      removeOverlayClasses();
    };
  }, [isTimesup, setIsTimesup, isVoucherPayment, voucherOrderData, voucherAmount]);

  const { isSticky, sentinelRef } = useSticky();

  useEffect(() => {
    if (isTimesup) {
      document.body.classList.add('overlay', 'overflow-hidden', 'overlay-index');
    } else {
      document.body.classList.remove('overlay', 'overflow-hidden', 'overlay-index');
    }
  }, [isTimesup]);

  return (
    <>
      <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
      {isVoucherPayment && (
        <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
          <Timer customClass={styles.timer} />
          <Header title="Select your payment method" onBack={handleBack} />
        </div>
      )}
      <section
        className={`${styles.paymentOptions} space-x container-md space-y cart-space-b`}
      >
        {!isTimesup && isVoucherPayment && (
          <div className={styles.paymentCard}>
            <PaymentMethod
              amount={voucherAmount}
              currency={SGD}
              voucherOrderData={voucherOrderData}
            />
          </div>
        )}
      </section>
      {isTimesup && (
        <SessionExpiredPopup
          active={isTimesup}
          onClose={handleSessionExpiredClose}
        />
      )}
    </>
  );
};

export default PaymentOptions;
